import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { RootState, AppDispatch, ExtraArgument } from '../index';
import { setupSignupListeners } from '@skynet/data-signup';
import { refreshTokenFailed } from '@skynet/data-auth-api';
import { POST_SIGNOUT_URL, SIGNIN_URL, SIGNOUT_URL } from '@skynet/auth-feature';
import { skyRouter } from 'src/routes/sky-router';

export const listenerMiddleware = createListenerMiddleware();
export const startAppListening = listenerMiddleware.startListening.withTypes<
  RootState,
  AppDispatch,
  ExtraArgument
>();

// Register package listeners
setupSignupListeners(startAppListening);

// Handle token refresh failures by navigating to signout.
// Only redirect on authentication errors (401 / 403 / 429) where the refresh token
// itself is rejected. Network errors, timeouts, and 5xx responses should be ignored
// so the user can stay on the page and retry once connectivity recovers.
//
// Guard against re-navigating if a signout is already in progress (committed or pending),
// which can happen when in-flight requests return 401 after a successful logout.
startAppListening({
  actionCreator: refreshTokenFailed,
  effect: async (action) => {
    const status = action.payload.status;
    const isAuthFailure = status === 401 || status === 403 || status === 429;
    if (!isAuthFailure) {
      return;
    }

    const router = skyRouter.router;
    const committedPath = router?.state.location.pathname ?? '';
    const pendingPath = router?.state.navigation.location?.pathname ?? '';
    const signoutPaths = [SIGNOUT_URL, POST_SIGNOUT_URL, SIGNIN_URL];
    if (signoutPaths.includes(committedPath) || signoutPaths.includes(pendingPath)) {
      return;
    }
    await router?.navigate(SIGNOUT_URL);
  },
});
